import { useState, useEffect, useRef, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from 'modules/state-mgmt/store';
import { fetchWorkerCourses } from 'modules/state-mgmt/workerData';
import { SortType, TabIndex, Course } from '../types';

const SORT_CRITERIA_MAP: Record<
  SortType,
  { sortBy: 'coursename' | 'completiondate' | 'expirationdate'; sortDirection: 'ASC' | 'DESC' }
> = {
  [SortType.NAME_ASC]: { sortBy: 'coursename', sortDirection: 'ASC' },
  [SortType.NAME_DESC]: { sortBy: 'coursename', sortDirection: 'DESC' },
  [SortType.COMPLETION_DATE_ASC]: { sortBy: 'completiondate', sortDirection: 'ASC' },
  [SortType.COMPLETION_DATE_DESC]: { sortBy: 'completiondate', sortDirection: 'DESC' },
  [SortType.EXPIRATION_DATE_ASC]: { sortBy: 'expirationdate', sortDirection: 'ASC' },
  [SortType.EXPIRATION_DATE_DESC]: { sortBy: 'expirationdate', sortDirection: 'DESC' },
};

export const useCourseList = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(TabIndex.ACTIVE);
  const [sortType, setSortType] = useState(SortType.NAME_ASC);
  const [page, setPage] = useState(1);
  const itemsPerPage = 9;
  const dispatch = useAppDispatch();
  const abortControllerRef = useRef<AbortController | null>(null);

  const { workerId, workerCourses, firstName, lastName, email, isWorker } = useAppSelector(state => ({
    workerId: state.onboarding?.id,
    workerCourses: state.workerData?.workerCourses,
    firstName: state.onboarding?.firstName,
    lastName: state.onboarding?.lastName,
    email: state.onboarding?.email,
    isWorker: state.user?.isWorker,
  }));

  const getCourseStatusFromTab = (tabIndex: number): 1 | 3 | undefined => {
    switch (tabIndex) {
      case TabIndex.ACTIVE:
        return 1;
      case TabIndex.EXPIRED:
        return 3;
      default:
        return undefined;
    }
  };

  const loadCourses = async (tabIndex: number, pageNumber: number = 1) => {
    if (!workerId) return;

    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setError(null);

    try {
      const courseStatus = getCourseStatusFromTab(tabIndex);
      const sortCriteria = SORT_CRITERIA_MAP[sortType];

      await dispatch(
        fetchWorkerCourses({
          workerId,
          courseStatus,
          pageNumber,
          pageSize: itemsPerPage,
          sortBy: sortCriteria.sortBy,
          sortDirection: sortCriteria.sortDirection,
        }),
      ).unwrap();
    } catch (err: any) {
      // Don't set error if request was aborted
      if (err?.name !== 'AbortError') {
        setError(err?.message || 'Failed to fetch courses');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Load courses when workerId, activeTab, or sortType changes
  useEffect(() => {
    if (workerId) {
      loadCourses(activeTab, 1);
      setPage(1);
    }

    // Cleanup function to abort ongoing requests on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [workerId, activeTab, sortType]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setError(null);
  };

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
    loadCourses(activeTab, pageNumber);
  };

  const allCourses = [...(workerCourses?.active || []), ...(workerCourses?.expired || [])];

  const courseCounts = useMemo(() => {
    const activeCount = workerCourses?.pagination?.active?.totalRecords ?? workerCourses?.active?.length ?? 0;
    const expiredCount = workerCourses?.pagination?.expired?.totalRecords ?? workerCourses?.expired?.length ?? 0;

    return {
      active: activeCount,
      expired: expiredCount,
    };
  }, [workerCourses]);

  const getPaginationInfo = () => {
    const statusKey = activeTab === TabIndex.ACTIVE ? 'active' : 'expired';
    return workerCourses?.pagination?.[statusKey];
  };

  const handleSortChange = (newSortType: SortType) => {
    setError(null);
    setPage(1);
    setSortType(newSortType);
  };

  return {
    courseList: allCourses,
    courseCounts,
    workerCourses,
    isLoading,
    error,
    activeTab,
    page,
    handleTabChange,
    handlePageChange,
    paginationInfo: getPaginationInfo(),
    handleSortChange,
    sortType,
    workerInfo: { firstName, lastName, email },
    isWorker,
  };
};
